import { Injectable, NgZone, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, Subscription, interval } from 'rxjs'

import { ApiNotificationsService } from '../api/api-notifications.service'
import { INotification } from '../../interfaces/notification.interface'

const POLL_MS = 30000

@Injectable({ providedIn: 'root' })
export class NotificationsViewService implements OnDestroy {
  private unreadSubject = new BehaviorSubject<number>(0)
  public unread$: Observable<number> = this.unreadSubject.asObservable()

  private listSubject = new BehaviorSubject<INotification[]>([])
  public list$: Observable<INotification[]> = this.listSubject.asObservable()

  private pollSub: Subscription | null = null
  private visibilityHandler = () => this.onVisibilityChange()

  constructor(private api: ApiNotificationsService, private zone: NgZone) {}

  //* arranca una sola vez (lo llama NotificationBellComponent en ngOnInit)
  startPolling(): void {
    if (this.pollSub) return
    this.refreshCount()
    //* runOutsideAngular evita disparar change-detection en cada tick ocioso
    this.zone.runOutsideAngular(() => {
      this.pollSub = interval(POLL_MS).subscribe(() => {
        if (document.visibilityState === 'visible') {
          this.zone.run(() => this.refreshCount())
        }
      })
    })
    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  private onVisibilityChange(): void {
    //* al volver a la pestaña, refresca de inmediato (no espera al siguiente tick)
    if (document.visibilityState === 'visible') this.zone.run(() => this.refreshCount())
  }

  refreshCount(): void {
    this.api.unreadCount().subscribe({
      next: (r) => this.unreadSubject.next((r?.data as any)?.count ?? (r?.data as any) ?? 0),
      //* un tick fallido (red caída, 5xx) NO debe dejar el badge colgado ni romper el polling.
      error: (err) => console.warn('[notifications] refreshCount falló, se conserva el último contador', err),
    })
  }

  loadList(): void {
    this.api.list(1, 20).subscribe({ next: (r) => this.listSubject.next((r?.data as any)?.data ?? (r?.data as any) ?? []) })
  }

  markRead(id: string): void {
    this.api.markRead(id).subscribe({
      next: () => {
        this.loadList()
        this.refreshCount()
      },
    })
  }

  markAllRead(): void {
    this.api.markAllRead().subscribe({
      next: () => {
        this.loadList()
        this.unreadSubject.next(0)
      },
    })
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe()
    document.removeEventListener('visibilitychange', this.visibilityHandler)
  }
}
